import BaseService from "@/services/BaseService.ts";
import {supabase} from "@supabaseClient";
import {SaccoLoan, SaccoLoanRepayment} from "@/@types/sacco.models.ts";
import { generateRepaymentSchedule } from '@saccoUtils';

/**
 * Represents a service for managing Sacco loans and their associated operations.
 * Extends the BaseService class to inherit basic service-related functionalities.
 */
export class SaccoLoanService extends BaseService {

    /**
     * Creates a loan for a user and generates a repayment schedule.
     *
     * @param {string} userId - The ID of the user creating the loan.
     * @param {string} membershipId - The ID of the user's membership associated with the loan.
     * @param {SaccoLoanCreateRequest} request - The loan request details including the loan amount, interest rate, and repayment terms.
     * @return {Promise<SaccoLoan>} A promise that resolves to the created Sacco loan object.
     */
    public async createLoan(userId: string, membershipId: string, request: SaccoLoanCreateRequest): Promise<SaccoLoan> {
        const payload = this.convertRequestToSnakeCase(request);
        try {
            // Insert the loan first.
            const { data: loan, error: loanError } = await supabase
                .from('sacco_loans')
                .insert({ ...payload, membership_id: membershipId, user_id: userId, status: 'active' })
                .select()
                .single();

            if (loanError) throw loanError;

            // Generate and batch-insert the repayment schedule.
            const schedule = generateRepaymentSchedule({
                principal: loan.principal,
                annualRate: loan.annual_interest_rate,
                installments: loan.total_installments,
                disbursementDate: new Date(loan.disbursement_date),
            });

            const repaymentRows = schedule.map((s) => ({
                loan_id: loan.id,
                user_id: userId,
                installment_number: s.installmentNumber,
                due_date: s.dueDate.toISOString().split('T')[0],
                installment_amount: s.installmentAmount,
                principal_portion: s.principalPortion,
                interest_portion: s.interestPortion,
                is_paid: false,
            }));

            const { error: repError } = await supabase.from('sacco_loan_repayments').insert(repaymentRows);
            if (repError) throw repError;

            return loan
        } catch (error) {
            console.error("Error creating Sacco loan", error);
            await this.handleError(error);
        }
    }

    /**
     * Updates an existing Sacco loan in the database with new details.
     *
     * @param {SaccoLoanUpdateRequest} request - The request object containing the loan details to update and its unique identifier.
     * @return {Promise<SaccoLoan>} A promise that resolves to the updated Sacco loan object.
     */
    async updateLoan(request: SaccoLoanUpdateRequest): Promise<SaccoLoan> {
        const payload = this.convertRequestToSnakeCase(request);
        try {
            const { data, error } = await supabase
                .from('sacco_loans')
                .update({ ...payload })
                .eq('id', request.id)
                .select()
                .single();
            if (error) throw error;
            return data;
        } catch (error) {
            console.error("Error updating a Sacco loan", error);
            await this.handleError(error);
        }
    }

    /**
     * Fetches a list of Sacco loans associated with a given user and membership ID.
     *
     * @param {string} userId - The ID of the user whose loans are being fetched.
     * @param {string} membershipId - The membership ID associated with the user's Sacco loans.
     * @return {Promise<SaccoLoan[]>} A promise that resolves to an array of SaccoLoan objects.
     */
    public async fetchLoans(userId: string, membershipId: string): Promise<SaccoLoan[]> {
        try {
            const { data, error } = await supabase
                .from('sacco_loans')
                .select('*')
                .eq('membership_id', membershipId)
                .eq('user_id', userId)
                .order('disbursement_date', { ascending: false });
            if (error) throw error;
            return data
        } catch (error) {
            console.error("Error fetching Sacco loans", error);
            await this.handleError(error);
        }
    }

    /**
     * Fetches a list of Sacco loan repayments for the specified user and membership.
     *
     * @param {string} userId - The ID of the user whose loan repayments are being fetched.
     * @param {string} membershipId - The membership ID associated with the loan repayments.
     * @return {Promise<SaccoLoanRepayment[]>} A promise that resolves to an array of Sacco loan repayment records.
     */
    public async fetchRepayments(userId: string, membershipId: string): Promise<SaccoLoanRepayment[]> {
        try {
            const { data, error } = await supabase
                .from('sacco_loan_repayments')
                .select('*, sacco_loans!inner(membership_id)')
                .eq('sacco_loans.membership_id', membershipId)
                .eq('user_id', userId);
            if (error) throw error;
            return data;
        } catch (error) {
            console.error("Error fetching Sacco loan repayments", error);
            await this.handleError(error);
        }
    }

    /**
     * Fetches all loan repayments for a given sacco loan for a user
     *
     * @param {string} userId User ID
     * @param {string} loanId Loan ID
     * @return {Promise<SaccoLoanRepayment[]>} List of sacco loan repayments
     */
    public async fetchAllRepaymentsForLoan(userId: string, loanId: string): Promise<SaccoLoanRepayment[]> {
        try {
            const { data, error } = await supabase
                .from('sacco_loan_repayments')
                .select('*')
                .eq('loan_id', loanId)
                .eq('user_id', userId)
                .order('installment_number', { ascending: true });
            if (error) throw error;
            return data;
        } catch (error) {
            console.error(`Error fetching Sacco loan repayments for loan ${loanId}`, error);
            await this.handleError(error);
        }
    }

    /**
     * Marks a sacco loan installment as paid.
     * @param request - The request object containing installmentId, loanId, and paidDate.
     * @returns A promise that resolves to a SaccoLoanInstallmentPaidResponse indicating the result of marking the installment as paid.
     */
    public async markInstallmentPaid(request: SaccoLoanInstallmentPaidRequest): Promise<SaccoLoanInstallmentPaidResponse> {
        const { installmentId, loanId, paidDate } = request;
        try {
            // Mark the installment as paid.
            const { error: repError } = await supabase
                .from('sacco_loan_repayments')
                .update({ is_paid: true, paid_date: paidDate })
                .eq('id', installmentId);
            if (repError) throw repError;

            // Check if all installments for this loan are now paid.
            const { data: remaining, error: checkError } = await supabase
                .from('sacco_loan_repayments')
                .select('id')
                .eq('loan_id', loanId)
                .eq('is_paid', false);
            if (checkError) throw checkError;

            // Auto-close the loan when all installments are paid.
            if (remaining.length === 0) {
                const { error: closeError } = await supabase
                    .from('sacco_loans')
                    .update({ status: 'closed' })
                    .eq('id', loanId);
                if (closeError) throw closeError;
            }

            return { loanId, allPaid: remaining.length === 0 };
        } catch (error) {
            console.error(`Error marking Sacco loan installment as paid for loan ${loanId}`, error);
            await this.handleError(error);
        }
    }

    /**
     * Fetches loan repayment information for a specific user and membership ID from the Sacco loan repayments database table.
     *
     * @param userId - The unique identifier of the user whose loan repayments are to be fetched.
     * @param membershipId - The membership identifier associated with the user's Sacco loans.
     * @return A Promise that resolves to an array of SaccoLoanRepayment objects representing the loan repayments for the user.
     */
    public async fetchLoanRepayments(userId: string, membershipId: string): Promise<SaccoLoanRepayment[]> {
        try {
            const { data, error } = await supabase
                .from('sacco_loan_repayments')
                .select('*, sacco_loans!inner(membership_id)')
                .eq('sacco_loans.membership_id', membershipId)
                .eq('user_id', userId);
            if (error) throw error;
            return data;
        } catch (error) {
            console.error("Error fetching Sacco loan repayments", error);
            await this.handleError(error);
        }
    }

    /**
     * Deletes a loan record with the specified ID from the `sacco_loans` table.
     * Repayments associated with the loan are automatically deleted using ON DELETE CASCADE.
     *
     * @param {string} id - The unique identifier of the loan to be deleted.
     * @return {Promise<void>} A promise that resolves when the loan is successfully deleted.
     * @throws Will log an error and handle it using the `handleError` method if the operation fails.
     */
    public async deleteLoan(id: string): Promise<void> {
        try {
            // Repayments are deleted via ON DELETE CASCADE on the DB.
            const { error } = await supabase.from('sacco_loans').delete().eq('id', id);
            if (error) throw error;
        } catch (error) {
            console.error("Error deleting Sacco loan", error);
            await this.handleError(error);
        }
    }
}

export default new SaccoLoanService();
